import { DistaContent, DistaPageHeaderProps } from "@sista/dista-ui";
import { SistaPageLayout } from "@sista/fe-commons";
import { IdmClientContext } from "@sista/idm-client";
import {
  LoadingGuard,
  ShowAccessDeniedMsg,
  SistaId,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";
import { useSpravaUkoluClient } from "../../../api/utils/useSpravaUkoluClient";
import { useLocalNavigation } from "../../../hooks/useLocalNavigation";
import { OrganizationChangeNoticeDetailContent } from "../../content/organization/OrganizationChangeNoticeDetailContent";

export const OrganizationChangeNoticeDetailScreen = () => {
  const { t } = useTranslation("ucet");
  const { organizationId, changeNoticeId } = useParams();
  const navigate = useNavigate();
  const localNavigationConfig = useLocalNavigation();
  const rej = useRejstrikClient();
  const ukolu = useSpravaUkoluClient();
  const { loggedAccount } = useContext(IdmClientContext);

  const [getOrganizationCall, getOrganizationState] = useSafeSistaAsync(
    rej.getOrganizationById,
  );
  const [getNoticeCall, getNoticeState] = useSafeSistaAsync(
    rej.getOrganizationChangeNoticeDetail,
  );
  const [getMyRelationsCall, getMyRelationsState] = useSafeSistaAsync(
    rej.getMyRelationsInOrganization,
  );
  const [listMyTasksCall, listMyTasksState] = useSafeSistaAsync(
    ukolu.listMyUnresolvedTasks,
  );

  const isProvider = useMemo(
    () => loggedAccount?.activeProfile?.type === "PROVIDER",
    [loggedAccount?.activeProfile?.type],
  );

  const isOrgManager = useMemo(
    () =>
      (getMyRelationsState.value ?? []).some(
        (r) =>
          SistaId.parse(r.targetUrn).id.toString() === organizationId &&
          r.role === "ORGANIZATION_MANAGER",
      ),
    [getMyRelationsState.value, organizationId],
  );

  const taskContext = useMemo(() => {
    if (!organizationId || !changeNoticeId) return [];
    return [
      new SistaId("organizations", organizationId).asUrn(),
      new SistaId("workflows", "organization_change_notice").asUrn(),
      new SistaId("organizationChangeNotice", changeNoticeId).asUrn(),
    ];
  }, [organizationId, changeNoticeId]);

  // Providers have immediate access; others need the relations check first.
  useEffect(() => {
    if (!organizationId || !changeNoticeId) return;
    if (isProvider) {
      getOrganizationCall({ organizationId });
      getNoticeCall({ organizationId, changeNoticeId });
    } else {
      getMyRelationsCall({ organizationId });
    }
  }, [
    organizationId,
    changeNoticeId,
    isProvider,
    getOrganizationCall,
    getNoticeCall,
    getMyRelationsCall,
  ]);

  // Once org-manager status is confirmed, fetch content and check tasks.
  useEffect(() => {
    if (!organizationId || !changeNoticeId || isProvider || !isOrgManager)
      return;
    getOrganizationCall({ organizationId });
    getNoticeCall({ organizationId, changeNoticeId });
    listMyTasksCall({ taskContext });
  }, [
    organizationId,
    changeNoticeId,
    isProvider,
    isOrgManager,
    taskContext,
    getOrganizationCall,
    getNoticeCall,
    listMyTasksCall,
  ]);

  const organization = useMemo(
    () => getOrganizationState.value,
    [getOrganizationState.value],
  );
  const notice = useMemo(() => getNoticeState.value, [getNoticeState.value]);

  const headerConfig: DistaPageHeaderProps = {
    breadcrumbs: [
      {
        title: t("ucet.menu.breadcrumbs.home", "Domů"),
        action: () => (window.location.href = "/ucet/"),
      },
      {
        title: organization?.displayName ?? "...",
        action: () =>
          navigate(`/sprava-organizace/${organizationId}/changeNotices`),
      },
      {
        title: t(
          "ucet.organizationChangeNotice.detail.breadcrumb",
          "Oznámení změny",
        ),
      },
    ],
    pageTitle: organization?.displayName,
  };

  return (
    <SistaPageLayout localNavigationConfig={localNavigationConfig}>
      <DistaContent headerConfig={headerConfig} fullWidth={true}>
        {isProvider ? (
          <LoadingGuard state={getOrganizationState}>
            <LoadingGuard state={getNoticeState}>
              {organization && notice && (
                <OrganizationChangeNoticeDetailContent
                  organization={organization}
                  notice={notice}
                  onBack={null}
                  canManageAttachments={false}
                  onNoticeUpdated={() =>
                    getNoticeCall({ organizationId, changeNoticeId })
                  }
                  onWorkflowAction={() => {
                    getNoticeCall({ organizationId, changeNoticeId });
                    listMyTasksCall({ taskContext });
                  }}
                />
              )}
            </LoadingGuard>
          </LoadingGuard>
        ) : (
          <LoadingGuard state={getMyRelationsState}>
            {isOrgManager ? (
              <LoadingGuard state={getOrganizationState}>
                <LoadingGuard state={getNoticeState}>
                  {organization && notice && (
                    <OrganizationChangeNoticeDetailContent
                      organization={organization}
                      notice={notice}
                      onBack={() =>
                        navigate(
                          `/sprava-organizace/${organizationId}/changeNotices`,
                        )
                      }
                      canManageAttachments={
                        (listMyTasksState.value?.length ?? 0) > 0
                      }
                      onNoticeUpdated={() =>
                        getNoticeCall({ organizationId, changeNoticeId })
                      }
                      onWorkflowAction={() => {
                        getNoticeCall({ organizationId, changeNoticeId });
                        listMyTasksCall({ taskContext });
                      }}
                    />
                  )}
                </LoadingGuard>
              </LoadingGuard>
            ) : (
              <ShowAccessDeniedMsg />
            )}
          </LoadingGuard>
        )}
      </DistaContent>
    </SistaPageLayout>
  );
};
